import type { ListingDetails } from 'app/types/listingDetails.type';
import {
  listingUtils_bathDisplay,
  listingUtils_bedDisplay,
  listingUtils_summaryPriceText,
} from 'app/shared/utils/listingUtils';

export function getListingSummaryDisplay(currentListing: ListingDetails) {
  const { active, isAllowedToRetain } = currentListing;
  const shouldHideDetails = active === false && isAllowedToRetain === false;

  const priceSummary = currentListing.pricing?.summary;
  const minMaxPriceBeds = currentListing.listingMinMaxPriceBeds;

  const priceObj =
    priceSummary?.priceLow || priceSummary?.priceHigh
      ? priceSummary
      : { priceLow: minMaxPriceBeds?.minPrice, priceHigh: minMaxPriceBeds?.maxPrice };

  const priceDisplay = listingUtils_summaryPriceText(priceObj);
  const showPrice = priceDisplay && priceDisplay !== 'Call';

  // listingMinMaxPriceBeds is a building-level aggregate that can include bed configs
  // with no available units, causing a mismatch with the bedroom tabs in UnitsModule
  // (which are derived from floorplans). Prefer floorplans as the source of truth,
  // then pricing.summary (like legacy HDP), then listingMinMaxPriceBeds as last resort.
  const floorplans = currentListing.floorplans;
  let minBeds: number | undefined;
  let maxBeds: number | undefined;

  if (floorplans && floorplans.length > 0) {
    const bedCounts = floorplans.map((fp) => fp.beds);
    minBeds = Math.min(...bedCounts);
    maxBeds = Math.max(...bedCounts);
  } else if (priceSummary?.bedsLow != null || priceSummary?.bedsHigh != null) {
    minBeds = priceSummary.bedsLow;
    maxBeds = priceSummary.bedsHigh;
  } else {
    minBeds = minMaxPriceBeds?.minBeds;
    maxBeds = minMaxPriceBeds?.maxBeds;
  }

  const bedRangeDisplay = listingUtils_bedDisplay(minBeds, maxBeds, undefined, '-');
  const showBedRange = bedRangeDisplay && bedRangeDisplay !== '';

  let minBaths: number | undefined;
  let maxBaths: number | undefined;

  if (floorplans && floorplans.length > 0) {
    const bathCounts = floorplans.map((fp) => fp.baths);
    minBaths = Math.min(...bathCounts);
    maxBaths = Math.max(...bathCounts);
  } else if (priceSummary?.bathsLow != null || priceSummary?.bathsHigh != null) {
    minBaths = priceSummary.bathsLow;
    maxBaths = priceSummary.bathsHigh;
  }

  const bathRangeDisplay = listingUtils_bathDisplay(minBaths, maxBaths, 'long', '-');
  const showBathRange = bathRangeDisplay && bathRangeDisplay !== '';

  const unitCount = currentListing.modelTypeUnitCount;
  const isRoomForRent = currentListing.listingType === 'room';
  const isSublet = currentListing.listingType === 'sublet';
  const isSingleUnit = currentListing.isUnit === true;
  const showUnits = unitCount && unitCount > 0 && !isRoomForRent && !isSublet && !isSingleUnit;

  const roomSqFt = currentListing.details?.roomForRent?.roomDetails?.roomSqFt;
  const roommatesCount = currentListing.details?.roomForRent?.roommateDetails?.roommatesCount;
  const showRoomSqFt = isRoomForRent && Boolean(roomSqFt);
  const showHousemates = isRoomForRent && Boolean(roommatesCount);
  const housematesLabel = roommatesCount === '1' ? 'housemate' : 'housemates';

  const address = currentListing.address;
  const addressDisplay = address?.hideStreet
    ? `${address.city}, ${address.state} ${address.zip}`
    : `${address?.street}, ${address?.city}, ${address?.state} ${address?.zip}`;

  const websiteUrl = currentListing.details?.webSite;
  const websiteLinkText = currentListing.displayName
    ? `Visit ${currentListing.displayName} website`
    : 'Visit property website';

  return {
    shouldHideDetails,
    priceDisplay,
    showPrice,
    bedRangeDisplay,
    showBedRange,
    bathRangeDisplay,
    showBathRange,
    unitCount,
    showUnits,
    roomSqFt,
    showRoomSqFt,
    roommatesCount,
    showHousemates,
    housematesLabel,
    addressDisplay,
    websiteUrl,
    websiteLinkText,
  };
}
